import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Button, Chip, Grid, Paper, Tab, Tabs, TextField, Typography } from '@mui/material'
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import ExportMenu from '../../Shared/ExportMenu'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import { getRfidItemTimeline } from '../../../services/productService'
import { mensagemDeErro } from '../../../services/apiClient'
import { formatDate } from '../../../utils/format'
import useUserNames from '../../../utils/useUserNames'

const EMPTY = '—'

const formatTime = value => {
  if (!value) return EMPTY
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return EMPTY
  return date.toLocaleTimeString('pt-BR')
}

/**
 * O operador entra na tela desde que o SSO reconheça o `startedByUserId` — o
 * cadastro é de lá, e `/users/lookup` traduz o GUID em nome.
 *
 * `equipmentId` continua fora: não existe cadastro de equipamento em base
 * nenhuma para traduzi-lo, e GUID cru é ruído, não rastreabilidade.
 */
const stageLabel = step => step.stageName || `Etapa ${step.order + 1}`

const RfidTimeline = () => {
  const { epc } = useParams()
  const navigate = useNavigate()
  const { notify, FeedbackSnackbar } = useFeedback()

  const [search, setSearch] = useState('')
  const [steps, setSteps] = useState([])
  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(false)

  const { nomeDe } = useUserNames(
    useMemo(() => steps.map(step => step.startedByUserId).filter(Boolean), [steps])
  )

  const fetchTimeline = useCallback(async () => {
    if (!epc) return
    setLoading(true)
    try {
      const response = await getRfidItemTimeline(epc)
      setSteps(response.data.result || [])
    } catch (error) {
      setSteps([])
      notify(
        mensagemDeErro(error, 'Não foi possível carregar a linha do tempo da etiqueta.'),
        'error'
      )
    } finally {
      setLoading(false)
    }
  }, [epc, notify])

  useEffect(() => {
    fetchTimeline()
  }, [fetchTimeline])

  const handleSearch = event => {
    event.preventDefault()
    if (!search.trim()) {
      notify('Informe o EPC / código RFID.', 'error')
      return
    }
    navigate(`/RfidTimeline/${encodeURIComponent(search.trim())}`)
  }

  const columns = [
    { id: 'date', label: 'Data', element: row => formatDate(row.startedAt) },
    { id: 'time', label: 'Hora', element: row => formatTime(row.startedAt) },
    { id: 'stage', label: 'Etapa', element: row => stageLabel(row) },
    { id: 'finishedAt', label: 'Conclusão', element: row => formatTime(row.finishedAt) },
    { id: 'result', label: 'Resultado', element: row => row.qualityStatusLabel || EMPTY },
    { id: 'serial', label: 'Etiqueta', element: row => row.serialCurrent || EMPTY },
    { id: 'lot', label: 'Lote', element: row => row.lot || EMPTY },
    { id: 'observation', label: 'Observação', element: row => row.observation || EMPTY },
  ]

  const exportColumns = [
    { header: 'Data', accessor: row => formatDate(row.startedAt) },
    { header: 'Hora', accessor: row => formatTime(row.startedAt) },
    { header: 'Etapa', accessor: row => stageLabel(row) },
    { header: 'Conclusão', accessor: row => formatTime(row.finishedAt) },
    { header: 'Resultado', accessor: row => row.qualityStatusLabel || EMPTY },
    { header: 'Etiqueta', accessor: row => row.serialCurrent || EMPTY },
    { header: 'Etiqueta anterior', accessor: row => row.serialPrevious || EMPTY },
    { header: 'Lote', accessor: row => row.lot || EMPTY },
    { header: 'Observação', accessor: row => row.observation || EMPTY },
  ]

  return (
    <Box>
      <PageHeader
        title="Linha do Tempo do RFID"
        subtitle={epc ? `Etiqueta ${epc}` : 'Informe uma etiqueta para ver o histórico completo'}
        actions={
          <>
            {epc && steps.length > 0 && (
              <ExportMenu
                rows={steps}
                columns={exportColumns}
                fileName={`linha-do-tempo-${epc}`}
                title="Linha do Tempo do RFID"
              />
            )}
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/RfidTracking')}
            >
              Voltar
            </Button>
          </>
        }
      />

      {!epc ? (
        <Paper variant="outlined" sx={{ p: 2 }} component="form" onSubmit={handleSearch}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                label="EPC / Código RFID"
                value={search}
                onChange={event => setSearch(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SearchIcon />}
                fullWidth
                sx={{ height: 56 }}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>
        </Paper>
      ) : (
        <>
          <Tabs value={tab} onChange={(event, value) => setTab(value)} sx={{ mb: 2 }}>
            <Tab label="Linha do tempo" />
            <Tab label="Tabela" />
          </Tabs>

          {tab === 0 ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              {steps.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {loading
                    ? 'Carregando eventos...'
                    : 'Nenhum evento registrado para esta etiqueta.'}
                </Typography>
              ) : (
                <Timeline sx={{ m: 0, p: 0 }}>
                  {steps.map((step, index) => (
                    <TimelineItem key={step.id}>
                      <TimelineOppositeContent sx={{ flex: 0.25 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDate(step.startedAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(step.startedAt)}
                        </Typography>
                      </TimelineOppositeContent>
                      <TimelineSeparator>
                        <TimelineDot color={index === steps.length - 1 ? 'success' : 'primary'} />
                        {index < steps.length - 1 && <TimelineConnector />}
                      </TimelineSeparator>
                      <TimelineContent sx={{ pb: 3 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {stageLabel(step)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Resultado: {step.qualityStatusLabel || EMPTY} · Conclusão:{' '}
                          {formatTime(step.finishedAt)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Etiqueta: {step.serialCurrent || EMPTY} · Lote: {step.lot || EMPTY}
                        </Typography>
                        {step.startedByUserId && (
                          <Typography variant="body2" color="text.secondary">
                            Operador: {nomeDe(step.startedByUserId)}
                          </Typography>
                        )}
                        {step.tagSwapped && (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                            label={`Etiqueta substituída — antes ${step.serialPrevious}`}
                          />
                        )}
                        {step.observation && (
                          <Typography variant="body2" color="text.secondary">
                            Observação: {step.observation}
                          </Typography>
                        )}
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </Timeline>
              )}
            </Paper>
          ) : (
            <DataTable
              columns={columns}
              rows={steps}
              loading={loading}
              pagination={false}
              rowKey={row => row.id}
              emptyMessage="Nenhum evento registrado para esta etiqueta."
            />
          )}
        </>
      )}

      <FeedbackSnackbar />
    </Box>
  )
}

export default RfidTimeline
